import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { FiGlobe } from "react-icons/fi";

/** Endonyms; the control supports 2–4 languages with no layout change. */
const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "pt", label: "Português" },
] as const;

type Props = {
  /** Footer variant on navy. */
  inverse?: boolean;
  /** Full width (mobile sheet). */
  block?: boolean;
  className?: string;
};

/**
 * Ghost language control (spec c.10): a native <select> with a real, visually
 * hidden <label> in the current language. `<html lang>` is synced by the
 * i18n `languageChanged` listener; the choice is persisted (try/catch).
 */
const LanguageSelector: React.FC<Props> = ({ inverse = false, block = false, className = "" }) => {
  const { t, i18n } = useTranslation();
  const id = `lang-${useId().replace(/:/g, "")}`;
  const current = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

  const change = (lng: string) => {
    i18n.changeLanguage(lng);
    document.documentElement.lang = lng;
    try {
      localStorage.setItem("i18nextLng", lng);
    } catch {
      /* storage unavailable (private mode): the choice lasts for this visit */
    }
  };

  const classes = ["lang", inverse && "lang--inverse", block && "lang--block", className]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes}>
      <label className="visually-hidden" htmlFor={id}>
        {t("header.language")}
      </label>
      <FiGlobe className="lang-icon" aria-hidden="true" focusable="false" />
      <select
        id={id}
        className="lang-select"
        value={current}
        onChange={(e) => change(e.target.value)}
      >
        {LANGUAGES.map((l) => (
          <option key={l.code} value={l.code} lang={l.code}>
            {l.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default LanguageSelector;
